<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import { doc, updateDoc } from 'firebase/firestore'
import { db } from '@/firebase'
import { useRoute } from 'vue-router'
import draggable from 'vuedraggable'
import { useOpsStore } from '@/stores/ops'
import { useTeamsStore } from '@/stores/teams'
import { usePersonnelStore } from '@/stores/personnel'
import { usePermissionsStore } from '@/stores/permissions'
import { useCurrentOpRole } from '@/composables/useCurrentOpRole'
import { useDragState } from '@/composables/useDragState'
import {
  TeamStatus,
  TeamType,
  PersonnelStatus,
  OverheadRole,
  SearcherType,
  RopeQual,
  MedicalQual,
  MiscQual,
  MEDICAL_QUAL_OPTIONS,
  SEARCHER_TYPE_OPTIONS,
  ROPE_QUAL_OPTIONS,
  STANDARD_MISC_QUALS,
} from '@/types'
import type { Personnel, Team } from '@/types'
import FosButton from '@/components/ui/FosButton.vue'
import FosInput from '@/components/ui/FosInput.vue'
import PermissionGuard from '@/components/ui/PermissionGuard.vue'
import CheckInModal from '@/components/personnel/CheckInModal.vue'
import QRCodePanel from '@/components/personnel/QRCodePanel.vue'
import CheckInStats from '@/components/personnel/CheckInStats.vue'
import DebriefModal from '@/components/assignments/DebriefModal.vue'
import OverheadTeamCard from '@/components/teams/OverheadTeamCard.vue'
import FieldTeamCard from '@/components/teams/FieldTeamCard.vue'
import TeamDetailDialog from '@/components/teams/TeamDetailDialog.vue'
import PersonCard from '@/components/teams/PersonCard.vue'
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItemIndicator,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from 'radix-vue'

const route = useRoute()
const incidentId = route.params.id as string

// ── Panel resize ───────────────────────────────────────────────────────────

const PANEL_WIDTH_KEY = 'fos_personnel_panel_width'
const MIN_PANEL_WIDTH = 180
const MIN_TEAMS_WIDTH = 240

const stored = parseInt(localStorage.getItem(PANEL_WIDTH_KEY) ?? '', 10)
const panelWidth = ref(isNaN(stored) ? 232 : stored)

let saveTimer: ReturnType<typeof setTimeout> | null = null

function savePanelWidth() {
  if (saveTimer) clearTimeout(saveTimer)
  saveTimer = setTimeout(() => {
    localStorage.setItem(PANEL_WIDTH_KEY, String(panelWidth.value))
  }, 100)
}

function onResizeMouseDown(e: MouseEvent) {
  e.preventDefault()
  const startX = e.clientX
  const startWidth = panelWidth.value

  function onMouseMove(ev: MouseEvent) {
    const delta = ev.clientX - startX
    const maxWidth = window.innerWidth - MIN_TEAMS_WIDTH - 5
    panelWidth.value = Math.max(MIN_PANEL_WIDTH, Math.min(startWidth + delta, maxWidth))
    savePanelWidth()
  }

  function onMouseUp() {
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
  }

  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
}

onUnmounted(() => {
  if (saveTimer) clearTimeout(saveTimer)
})

const opsStore = useOpsStore()
const teamsStore = useTeamsStore()
const personnelStore = usePersonnelStore()
const permissionsStore = usePermissionsStore()
const { opRole } = useCurrentOpRole()
const { startDrag, endDrag, isDragging } = useDragState()

const teamCount = computed(() => teamsStore.teams.length)
const canManageTeams = computed(() => permissionsStore.can('team.create_manage', opRole.value))
const canUpdateStatus = computed(() => permissionsStore.can('team.update_status', opRole.value))
const canCheckIn = computed(() => permissionsStore.can('personnel.checkin', opRole.value))
const canGenerateQR = computed(() => permissionsStore.can('personnel.generate_qr', opRole.value))
async function handleCreateTeam() {
  if (opsStore.selectedOpId) {
    await teamsStore.createTeam(incidentId, opsStore.selectedOpId)
  }
}

// ── Check-in modal ─────────────────────────────────────────────────────────

const showCheckIn = ref(false)
const showQR = ref(false)

// ── Grouping ───────────────────────────────────────────────────────────────

const grouped = computed(() => ({
  available: filteredPersonnel.value.filter((p) => p.status === PersonnelStatus.Available),
  assigned: filteredPersonnel.value.filter((p) => p.status === PersonnelStatus.Assigned),
  outOfService: filteredPersonnel.value.filter((p) => p.status === PersonnelStatus.OutOfService),
}))

const collapsed = ref({
  available: false,
  assigned: true,
  outOfService: true,
})

// ── Qualification filter ────────────────────────────────────────────────────

const qualFilter = ref<string[]>([])

function extractQuals(p: Personnel): string[] {
  const q = p.qualifications
  const result: string[] = []
  if (q.searcher_type && q.searcher_type !== 'None') result.push(q.searcher_type as string)
  if (q.rope_qual && q.rope_qual !== 'None') result.push(q.rope_qual as string)
  if (q.medical_qual && q.medical_qual !== 'None') result.push(q.medical_qual as string)
  if (Array.isArray(q.misc_quals)) result.push(...(q.misc_quals as string[]))
  return result
}

// Build grouped options — only include values present in the current roster
const qualFilterOptions = computed(() => {
  const presentQuals = new Set<string>()
  for (const p of personnelStore.roster) {
    for (const q of extractQuals(p)) presentQuals.add(q)
  }

  const groups = [
    { label: 'Medical', items: MEDICAL_QUAL_OPTIONS },
    { label: 'Searcher Type', items: SEARCHER_TYPE_OPTIONS },
    { label: 'Rope', items: ROPE_QUAL_OPTIONS },
    { label: 'Other', items: STANDARD_MISC_QUALS },
  ]

  return groups
    .map((g) => ({
      label: g.label,
      items: g.items
        .filter((q) => presentQuals.has(q))
        .map((q) => ({ label: q, value: q })),
    }))
    .filter((g) => g.items.length > 0)
})

// ── Personnel search ────────────────────────────────────────────────────────

const personnelSearch = ref('')

const filteredPersonnel = computed(() => {
  let list = personnelStore.roster
  if (qualFilter.value.length > 0) {
    list = list.filter((p) => qualFilter.value.some((q) => extractQuals(p).includes(q)))
  }
  if (personnelSearch.value.trim()) {
    const q = personnelSearch.value.trim().toLowerCase()
    list = list.filter(
      (p) =>
        `${p.first} ${p.last}`.toLowerCase().includes(q) ||
        (p.agency ?? '').toLowerCase().includes(q),
    )
  }
  return list
})

watch(personnelSearch, () => {
  if (!personnelSearch.value.trim()) {
    collapsed.value.assigned = true
    collapsed.value.outOfService = true
    return
  }
  if (grouped.value.assigned.length > 0) collapsed.value.assigned = false
  if (grouped.value.outOfService.length > 0) collapsed.value.outOfService = false
})

const overheadTeams = computed(() =>
  teamsStore.teams.filter((t) => t.team_type === TeamType.Overhead),
)

const filteredOverheadTeams = computed(() => {
  const q = personnelSearch.value.trim().toLowerCase()
  if (!q) return overheadTeams.value
  return overheadTeams.value.filter((team) => {
    const teamPersonnel = personnelStore.roster.filter((p) => p.team_id === team.team_id)
    return teamPersonnel.some(
      (p) =>
        `${p.first} ${p.last}`.toLowerCase().includes(q) ||
        (p.agency ?? '').toLowerCase().includes(q),
    )
  })
})

const notAssignedStatuses = new Set([TeamStatus.Building, TeamStatus.AwaitingAssignment])

function sortByIdentifier<T extends { team: Team }>(groups: T[]): T[] {
  return [...groups].sort((a, b) => {
    const na = parseInt(a.team.team_identifier, 10)
    const nb = parseInt(b.team.team_identifier, 10)
    if (!isNaN(na) && !isNaN(nb)) return na - nb
    return a.team.team_identifier.localeCompare(b.team.team_identifier)
  })
}

const allFieldTeamGroups = computed(() =>
  teamsStore.teams
    .filter((t) => t.team_type !== TeamType.Overhead)
    .map((team) => ({
      team,
      leader: personnelStore.roster.find(
        (p) => p.personnel_id === team.team_leader_personnel_id,
      ) ?? null,
      members: personnelStore.roster.filter(
        (p) => p.team_id === team.team_id && p.personnel_id !== team.team_leader_personnel_id,
      ),
    })),
)
const filteredFieldTeamGroups = computed(() => {
  const q = personnelSearch.value.trim().toLowerCase()
  if (!q) return allFieldTeamGroups.value
  return allFieldTeamGroups.value.filter(({ leader, members }) => {
    const all = [...members, ...(leader ? [leader] : [])]
    return all.some(
      (p) =>
        `${p.first} ${p.last}`.toLowerCase().includes(q) ||
        (p.agency ?? '').toLowerCase().includes(q),
    )
  })
})

// Single-pass partition by status — O(n) total instead of O(n × statuses)
const fieldTeamGroupsByStatus = computed(() => {
  const map = new Map<TeamStatus, typeof allFieldTeamGroups.value>()
  for (const g of filteredFieldTeamGroups.value) {
    const list = map.get(g.team.team_status)
    if (list) list.push(g)
    else map.set(g.team.team_status, [g])
  }
  for (const [status, list] of map) map.set(status, sortByIdentifier(list))
  return map
})

function byStatus(...statuses: TeamStatus[]) {
  return statuses.flatMap((s) => fieldTeamGroupsByStatus.value.get(s) ?? [])
}

const fieldTeamGroupsBuilding           = computed(() => byStatus(TeamStatus.Building))
const fieldTeamGroupsAwaitingAssignment = computed(() => byStatus(TeamStatus.AwaitingAssignment))
const fieldTeamGroupsAssigned           = computed(() => byStatus(TeamStatus.Assigned))
const fieldTeamGroupsEnroute            = computed(() => byStatus(TeamStatus.Enroute))
const fieldTeamGroupsOnScene            = computed(() => byStatus(TeamStatus.OnScene))
const fieldTeamGroupsRTB                = computed(() => byStatus(TeamStatus.RTB))
const fieldTeamGroupsReturned           = computed(() => byStatus(TeamStatus.Returned))
const fieldTeamGroupsEmergency          = computed(() => byStatus(TeamStatus.Emergency))

const fieldTeamGroupsNotAssigned = computed(() =>
  byStatus(TeamStatus.Building, TeamStatus.AwaitingAssignment),
)
const fieldTeamGroupsOther = computed(() =>
  sortByIdentifier(
    [...fieldTeamGroupsByStatus.value.entries()]
      .filter(([s]) => !notAssignedStatuses.has(s))
      .flatMap(([, list]) => list),
  ),
)
const fieldTeamGroups = computed(() => [...fieldTeamGroupsNotAssigned.value, ...fieldTeamGroupsOther.value])

// ── Unassign helper (used by the unassigned drag zone) ─────────────────────

async function doUnassignFromTeam(person: Personnel) {
  const opId = opsStore.selectedOpId!
  await teamsStore.unassignMemberFromTeam(incidentId, opId, person.user_id, person.personnel_id)
  await updateDoc(
    doc(db, 'incidents', incidentId, 'operational_periods', opId, 'personnel', person.personnel_id),
    { team_id: null, status: PersonnelStatus.Available },
  )
}

// ── Drag and drop: unassigned zone ─────────────────────────────────────────────

async function handleUnassignedChange(event: { added?: { element: Personnel } }) {
  if (!event.added) return
  const person = event.added.element

  // Clear leader role if this person was leading any team
  const leadingTeam = teamsStore.teams.find(
    (t) => t.team_leader_personnel_id === person.personnel_id,
  )
  if (leadingTeam) {
    await teamsStore.setTeamLeader(incidentId, opsStore.selectedOpId!, leadingTeam.team_id, null)
  }

  await doUnassignFromTeam(person)
}

// ── Debrief ────────────────────────────────────────────────────────────────

const debriefTeam = ref<Team | null>(null)

// ── Team detail dialog ─────────────────────────────────────────────────────

const detailTeam = ref<Team | null>(null)
const detailDialogVisible = computed({
  get: () => detailTeam.value !== null,
  set: (v) => { if (!v) detailTeam.value = null },
})

// ── Dev seed (beta testers only) ───────────────────────────────────────────

const isBetaTester = localStorage.getItem('beta_unlocked') === 'true'
const seedLoading = ref(false)
const seedDone = ref(false)

const DUMMY_PERSONNEL = [
  { joinAs: 'field' as const, data: { user_id: null, last: 'Tūhoe', first: 'Rawiri', agency: 'Wellington LandSAR', phone: '021 555 0101', when_made: '', resource: '', qualifications: { medical_qual: MedicalQual.NurseParamedic, searcher_type: SearcherType.Type1, rope_qual: RopeQual.Operator, misc_quals: [MiscQual.Overhead] }, category: '', team_id: null, notes: '' } },
  { joinAs: 'field' as const, data: { user_id: null, last: 'Parāone', first: 'Hemi', agency: 'Nelson Marlborough LandSAR', phone: '021 555 0102', when_made: '', resource: '', qualifications: { medical_qual: MedicalQual.EMR, searcher_type: SearcherType.Type1, rope_qual: RopeQual.Awareness, misc_quals: [MiscQual.Overhead] }, category: '', team_id: null, notes: '' } },
  { joinAs: 'field' as const, data: { user_id: null, last: 'Walker', first: 'Aroha', agency: 'Canterbury LandSAR', phone: '021 555 0103', when_made: '', resource: '', qualifications: { medical_qual: MedicalQual.EMR, searcher_type: SearcherType.Type2, rope_qual: RopeQual.None, misc_quals: [MiscQual.UAV] }, category: '', team_id: null, notes: '' } },
  { joinAs: 'field' as const, data: { user_id: null, last: 'Ngata', first: 'Tāmati', agency: 'Tasman LandSAR', phone: '021 555 0104', when_made: '', resource: '', qualifications: { medical_qual: MedicalQual.None, searcher_type: null, rope_qual: RopeQual.None, misc_quals: [] }, category: '', team_id: null, notes: '' } },
  { joinAs: 'field' as const, data: { user_id: null, last: 'Bennett', first: 'Mere', agency: 'Wellington LandSAR', phone: '021 555 0105', when_made: '', resource: '', qualifications: { medical_qual: MedicalQual.EMR, searcher_type: null, rope_qual: RopeQual.None, misc_quals: [] }, category: '', team_id: null, notes: '' } },
  { joinAs: 'field' as const, data: { user_id: null, last: 'Hipango', first: 'Pita', agency: 'Wellington LandSAR', phone: '021 555 0201', when_made: '', resource: '', qualifications: { medical_qual: MedicalQual.EMT, searcher_type: SearcherType.Type1, rope_qual: RopeQual.Technician, misc_quals: [MiscQual.SwiftWater] }, category: '', team_id: null, notes: '' } },
  { joinAs: 'field' as const, data: { user_id: null, last: 'Parata', first: 'Anika', agency: 'Canterbury LandSAR', phone: '021 555 0202', when_made: '', resource: '', qualifications: { medical_qual: MedicalQual.EMR, searcher_type: SearcherType.Type2, rope_qual: RopeQual.Operator, misc_quals: [MiscQual.TrackingTrailing] }, category: '', team_id: null, notes: '' } },
  { joinAs: 'field' as const, data: { user_id: null, last: "O'Sullivan", first: 'Ciarán', agency: 'Otago LandSAR', phone: '021 555 0203', when_made: '', resource: '', qualifications: { medical_qual: MedicalQual.None, searcher_type: SearcherType.Type2, rope_qual: RopeQual.None, misc_quals: [MiscQual.K9] }, category: '', team_id: null, notes: 'Handler — German Shepherd "Rua"' } },
  { joinAs: 'field' as const, data: { user_id: null, last: 'Hadfield', first: 'Jack', agency: 'Marlborough LandSAR', phone: '021 555 0204', when_made: '', resource: '', qualifications: { medical_qual: MedicalQual.EMR, searcher_type: SearcherType.Type1, rope_qual: RopeQual.Awareness, misc_quals: [] }, category: '', team_id: null, notes: '' } },
  { joinAs: 'field' as const, data: { user_id: null, last: 'Tūhoe', first: 'Ngāhuia', agency: 'Wellington LandSAR', phone: '021 555 0205', when_made: '', resource: '', qualifications: { medical_qual: MedicalQual.NurseParamedic, searcher_type: SearcherType.Type1, rope_qual: RopeQual.Technician, misc_quals: [MiscQual.SwiftWater] }, category: '', team_id: null, notes: '' } },
  { joinAs: 'field' as const, data: { user_id: null, last: 'Fraser', first: 'Callum', agency: 'Southland LandSAR', phone: '021 555 0206', when_made: '', resource: '', qualifications: { medical_qual: MedicalQual.None, searcher_type: SearcherType.Type3, rope_qual: RopeQual.None, misc_quals: [MiscQual.UTV] }, category: '', team_id: null, notes: '' } },
  { joinAs: 'field' as const, data: { user_id: null, last: 'Iti', first: 'Rangi', agency: 'Tasman LandSAR', phone: '021 555 0207', when_made: '', resource: '', qualifications: { medical_qual: MedicalQual.EMR, searcher_type: SearcherType.Type2, rope_qual: RopeQual.Operator, misc_quals: [MiscQual.UAV] }, category: '', team_id: null, notes: '' } },
  { joinAs: 'field' as const, data: { user_id: null, last: 'Mackenzie', first: 'Isla', agency: 'Canterbury LandSAR', phone: '021 555 0208', when_made: '', resource: '', qualifications: { medical_qual: MedicalQual.EMT, searcher_type: SearcherType.Type1, rope_qual: RopeQual.Technician, misc_quals: [] }, category: '', team_id: null, notes: '' } },
  { joinAs: 'field' as const, data: { user_id: null, last: 'Pōhatu', first: 'Tūmataiti', agency: 'Otago LandSAR', phone: '021 555 0209', when_made: '', resource: '', qualifications: { medical_qual: MedicalQual.None, searcher_type: SearcherType.Type3, rope_qual: RopeQual.None, misc_quals: [] }, category: '', team_id: null, notes: '' } },
  { joinAs: 'field' as const, data: { user_id: null, last: 'Henare', first: 'Whiti', agency: 'Nelson Marlborough LandSAR', phone: '021 555 0210', when_made: '', resource: '', qualifications: { medical_qual: MedicalQual.EMR, searcher_type: SearcherType.Type2, rope_qual: RopeQual.Awareness, misc_quals: [MiscQual.TrackingTrailing, MiscQual.K9] }, category: '', team_id: null, notes: 'Dual-certified K9 tracker' } },
]

async function seedPersonnel() {
  const opId = opsStore.selectedOpId
  if (!opId) return
  seedLoading.value = true
  seedDone.value = false
  try {
    for (const entry of DUMMY_PERSONNEL) {
      await personnelStore.checkIn(
        incidentId,
        opId,
        { ...entry.data, when_made: new Date().toISOString() },
        entry.joinAs,
        undefined,
      )
    }
    seedDone.value = true
  } catch (e) {
    console.error('[DevSeed] Personnel seed failed:', e)
  } finally {
    seedLoading.value = false
  }
}

function toggleQualFilter(value: string) {
  const idx = qualFilter.value.indexOf(value)
  if (idx === -1) qualFilter.value.push(value)
  else qualFilter.value.splice(idx, 1)
}
</script>

<template>
  <div class="h-full flex flex-col overflow-hidden">
    <div class="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
      <div>
        <h2 class="font-headline font-black text-lg uppercase tracking-tight text-on-surface">Teams</h2>
        <p class="font-mono text-xs text-outline mt-0.5">
          {{personnelStore.onSceneCount}} personnel checked in, {{ teamCount }} team{{ teamCount !== 1 ? 's' : '' }} this operational period
        </p>
      </div>
      <div class="flex items-center gap-2 shrink-0">
        <PermissionGuard v-if="opsStore.selectedOpId" action="team.create_manage" :role="opRole">
          <FosButton variant="primary" size="sm" @click="handleCreateTeam">
            <span class="material-symbols-outlined text-[16px]">add</span>
            New Team
          </FosButton>
        </PermissionGuard>
        <button
          v-if="isBetaTester"
          class="text-amber-400 font-mono text-[0.65rem] px-3 py-1.5 disabled:opacity-40 transition-colors shrink-0"
          :disabled="seedLoading"
          @click="seedPersonnel"
          :style="{ cursor: seedLoading ? 'auto' : 'pointer' }"
        >
          <span v-if="seedLoading" class="w-3 h-3 border-2 border-amber-500/30 border-t-amber-400 animate-spin rounded-full" />
          <span v-else class="material-symbols-outlined text-[14px]">science</span>
        </button>
      </div>
    </div>

    <template v-if="opsStore.selectedOpId">
      <div class="flex flex-1 overflow-hidden px-6 py-4">
          <!-- Left panel: all personnel by status (hidden on mobile) -->
          <div
            class="hidden md:block shrink-0 overflow-y-auto"
            :style="{ width: `${panelWidth}px`, minWidth: `${MIN_PANEL_WIDTH}px` }"
          >
            <div class="@container flex flex-col gap-2">
            <!-- Toolbar: 2 rows (narrow) → 1 row (wide, @[434px] = PersonCard @[400px] content-box + p-3 padding 24px + draggable border+padding 10px) -->
            <div class="flex flex-col @[434px]:flex-row gap-2 shrink-0">
              <!-- Search + Filter (stretches to fill leftover space in wide mode) -->
              <div class="flex @[434px]:flex-1 border border-border rounded overflow-visible">
                <FosInput
                  v-model="personnelSearch"
                  placeholder="Search personnel…"
                  class="flex-1 border-0 rounded-none"
                />
                <DropdownMenuRoot>
                  <DropdownMenuTrigger
                    class="flex items-center justify-center px-2 bg-surface-container-high hover:bg-surface-container-highest border-l border-border disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none"
                    :disabled="qualFilterOptions.length === 0"
                    :title="qualFilter.length > 0 ? `${qualFilter.length} qual${qualFilter.length > 1 ? 's' : ''} selected` : 'Filter by qual'"
                  >
                    <span
                      class="material-symbols-outlined text-[18px]"
                      :class="qualFilter.length > 0 ? 'text-primary' : 'text-outline'"
                    >filter_list</span>
                  </DropdownMenuTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuContent
                      class="z-50 bg-surface-container-high border border-border min-w-[180px] shadow-xl max-h-64 overflow-y-auto"
                      align="end"
                      :side-offset="2"
                    >
                      <template v-for="group in qualFilterOptions" :key="group.label">
                        <DropdownMenuLabel class="px-3 pt-2 pb-1 text-[10px] font-mono uppercase tracking-widest text-outline">
                          {{ group.label }}
                        </DropdownMenuLabel>
                        <DropdownMenuCheckboxItem
                          v-for="opt in group.items"
                          :key="opt.value"
                          :checked="qualFilter.includes(opt.value)"
                          class="flex items-center gap-2 px-3 py-1.5 font-mono text-xs text-on-surface hover:bg-surface-container-highest focus:bg-surface-container-highest cursor-pointer focus:outline-none data-[highlighted]:bg-surface-container-highest"
                          @update:checked="() => toggleQualFilter(opt.value)"
                        >
                          <DropdownMenuItemIndicator>
                            <span class="material-symbols-outlined text-primary text-[14px]">check</span>
                          </DropdownMenuItemIndicator>
                          {{ opt.label }}
                        </DropdownMenuCheckboxItem>
                      </template>
                      <template v-if="qualFilter.length > 0">
                        <DropdownMenuSeparator class="h-px bg-surface-container my-1" />
                        <DropdownMenuItem
                          class="px-3 py-1.5 font-mono text-xs text-outline hover:text-on-surface hover:bg-surface-container-highest cursor-pointer focus:outline-none data-[highlighted]:bg-surface-container-highest"
                          @select="qualFilter = []"
                        >
                          Clear filter
                        </DropdownMenuItem>
                      </template>
                    </DropdownMenuContent>
                  </DropdownMenuPortal>
                </DropdownMenuRoot>
              </div>

              <!-- Check In + QR Code buttons -->
              <div class="flex gap-2 shrink-0">
                <PermissionGuard action="personnel.checkin" :role="opRole">
                  <FosButton variant="primary" class="flex-1 @[408px]:flex-none" @click="showCheckIn = true">
                    <span class="material-symbols-outlined text-[16px]">person_add</span>
                    Add T-Card
                  </FosButton>
                </PermissionGuard>
                <PermissionGuard action="personnel.generate_qr" :role="opRole">
                  <FosButton variant="secondary" @click="showQR = true">
                    <span class="material-symbols-outlined text-[16px]">qr_code</span>
                  </FosButton>
                </PermissionGuard>
              </div>
            </div>

            <!-- Available group (expanded by default) -->
            <div class="flex flex-col gap-1 shrink-0">
              <button
                class="flex items-center justify-between w-full text-xs font-medium uppercase tracking-wide text-outline hover:text-on-surface-variant py-0.5"
                @click="collapsed.available = !collapsed.available"
              >
                <span>Available <span class="normal-case font-normal">({{ grouped.available.length }})</span></span>
                <span class="material-symbols-outlined text-[14px]">{{ collapsed.available ? 'expand_more' : 'expand_less' }}</span>
              </button>
              <template v-if="!collapsed.available">
                  <draggable
                    :model-value="grouped.available"
                    :group="{ name: 'personnel' }"
                    item-key="personnel_id"
                    ghost-class="drag-ghost--card"
                    drag-class="drag-cursor--chip"
                    :force-fallback="true"
                    :fallback-on-body="true"
                    class="flex flex-col gap-1.5 rounded-sm p-1 min-h-[36px] border border-dashed transition-colors"
                    :class="isDragging ? 'border-primary/50 bg-primary/5' : 'border-transparent'"
                    @start="startDrag"
                    @end="endDrag"
                    @change="(e: { added?: { element: Personnel } }) => handleUnassignedChange(e)"
                  >
                    <template #item="{ element }">
                      <PersonCard :person="element" />
                    </template>
                  </draggable>
              </template>
            </div>

            <!-- Assigned group (collapsed by default) -->
            <div class="flex flex-col gap-1 shrink-0">
              <button
                class="flex items-center justify-between w-full text-xs font-medium uppercase tracking-wide text-outline hover:text-on-surface-variant transition-colors py-0.5"
                @click="collapsed.assigned = !collapsed.assigned"
              >
                <span>Assigned <span class="normal-case font-normal">({{ grouped.assigned.length }})</span></span>
                <span class="material-symbols-outlined text-[14px]">{{ collapsed.assigned ? 'expand_more' : 'expand_less' }}</span>
              </button>
              <template v-if="!collapsed.assigned">
                  <draggable
                    :model-value="grouped.assigned"
                    :group="{ name: 'personnel' }"
                    item-key="personnel_id"
                    ghost-class="drag-ghost--card"
                    drag-class="drag-cursor--chip"
                    :force-fallback="true"
                    :fallback-on-body="true"
                    class="flex flex-col gap-1.5 min-h-[36px] rounded-sm p-1"
                    @start="startDrag"
                    @end="endDrag"
                    @change="(e: { added?: { element: Personnel } }) => handleUnassignedChange(e)"
                  >
                    <template #item="{ element }">
                      <PersonCard :person="element" />
                    </template>
                  </draggable>
                <p v-if="grouped.assigned.length === 0" class="text-xs text-outline italic text-center py-2">No assigned personnel</p>
              </template>
            </div>

            <!-- Out of Service group (collapsed by default, non-draggable) -->
            <div class="flex flex-col gap-1 shrink-0">
              <button
                class="flex items-center justify-between w-full text-xs font-medium uppercase tracking-wide text-outline hover:text-on-surface-variant transition-colors py-0.5"
                @click="collapsed.outOfService = !collapsed.outOfService"
              >
                <span>Released<span class="normal-case font-normal">({{ grouped.outOfService.length }})</span></span>
                <span class="material-symbols-outlined text-[14px]">{{ collapsed.outOfService ? 'expand_more' : 'expand_less' }}</span>
              </button>
              <template v-if="!collapsed.outOfService">
                <div class="flex flex-col gap-1.5">
                  <PersonCard
                    v-for="person in grouped.outOfService"
                    :key="person.personnel_id"
                    :person="person"
                    disabled
                  />
                </div>
                <p v-if="grouped.outOfService.length === 0" class="text-xs text-outline italic text-center py-2">No out of service personnel</p>
              </template>
            </div>
            </div>
          </div>

          <!-- Resize handle (hidden on mobile) -->
          <div
            class="hidden md:block w-[5px] shrink-0 self-stretch cursor-col-resize bg-border hover:bg-primary transition-colors mx-1"
            @mousedown="onResizeMouseDown"
          />

          <!-- Right panel: teams -->
          <div class="flex-1 @container flex flex-col gap-4 overflow-y-auto min-w-[240px]">
            <!-- Overhead team cards (full-width, special layout) -->
            <OverheadTeamCard
              v-for="team in filteredOverheadTeams"
              :key="team.team_id"
              :incident-id="incidentId"
              :op-id="opsStore.selectedOpId"
              :team="team"
              :search-query="personnelSearch"
              @view-details="detailTeam = team"
            />
            <!-- Emergency (highest priority — always shown at top if present) -->
            <template v-if="fieldTeamGroupsEmergency.length > 0">
              <div class="flex flex-col items-center gap-1.5 shrink-0">
                <div class="w-full h-5 rounded flex items-center justify-center bg-error animate-pulse">
                  <span class="text-sm font-semibold text-on-error uppercase tracking-widest">Emergency</span>
                </div>
              </div>
              <div class="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3">
                <FieldTeamCard
                  v-for="group in fieldTeamGroupsEmergency"
                  :key="group.team.team_id"
                  :incident-id="incidentId"
                  :op-id="opsStore.selectedOpId"
                  :team="group.team"
                  :leader="group.leader"
                  :members="group.members"
                  :can-update-status="canUpdateStatus"
                  :search-query="personnelSearch"
                  @debrief="debriefTeam = group.team"
                  @view-details="detailTeam = group.team"
                />
              </div>
            </template>

            <!-- Building -->
            <div v-if="fieldTeamGroupsBuilding.length > 0" class="flex flex-col items-center gap-1.5 shrink-0">
              <div class="caution-stripe w-full h-5 rounded flex items-center justify-center">
                <span class="text-sm font-semibold text-on-surface-variant uppercase tracking-widest">Building</span>
              </div>
            </div>
            <div v-if="fieldTeamGroupsBuilding.length > 0 || canManageTeams" class="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3">
              <button
                v-if="canManageTeams"
                class="border border-dashed border-border rounded-sm flex flex-col items-center justify-center gap-2 min-h-[140px] text-outline hover:border-primary hover:text-primary transition-colors cursor-pointer"
                @click="handleCreateTeam"
              >
                <span class="material-symbols-outlined text-[32px]">add</span>
                <span class="font-mono text-xs uppercase tracking-widest">Create new team</span>
              </button>
              <FieldTeamCard
                v-for="group in fieldTeamGroupsBuilding"
                :key="group.team.team_id"
                :incident-id="incidentId"
                :op-id="opsStore.selectedOpId"
                :team="group.team"
                :leader="group.leader"
                :members="group.members"
                :can-update-status="canUpdateStatus"
                :search-query="personnelSearch"
                @debrief="debriefTeam = group.team"
                @view-details="detailTeam = group.team"
              />
            </div>

            <!-- Awaiting Assignment -->
            <template v-if="fieldTeamGroupsAwaitingAssignment.length > 0">
              <div class="flex flex-col items-center gap-1.5 shrink-0">
                <div class="caution-stripe w-full h-5 rounded flex items-center justify-center">
                  <span class="text-sm font-semibold text-on-surface-variant uppercase tracking-widest">Awaiting Assignment</span>
                </div>
              </div>
              <div class="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3">
                <FieldTeamCard
                  v-for="group in fieldTeamGroupsAwaitingAssignment"
                  :key="group.team.team_id"
                  :incident-id="incidentId"
                  :op-id="opsStore.selectedOpId"
                  :team="group.team"
                  :leader="group.leader"
                  :members="group.members"
                  :can-update-status="canUpdateStatus"
                  :search-query="personnelSearch"
                  @debrief="debriefTeam = group.team"
                  @view-details="detailTeam = group.team"
                />
              </div>
            </template>

            <!-- Assigned -->
            <template v-if="fieldTeamGroupsAssigned.length > 0">
              <div class="flex flex-col items-center gap-1.5 shrink-0">
                <div class="caution-stripe w-full h-5 rounded flex items-center justify-center">
                  <span class="text-sm font-semibold text-on-surface-variant uppercase tracking-widest">Assigned</span>
                </div>
              </div>
              <div class="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3">
                <FieldTeamCard
                  v-for="group in fieldTeamGroupsAssigned"
                  :key="group.team.team_id"
                  :incident-id="incidentId"
                  :op-id="opsStore.selectedOpId"
                  :team="group.team"
                  :leader="group.leader"
                  :members="group.members"
                  :can-update-status="canUpdateStatus"
                  :search-query="personnelSearch"
                  @debrief="debriefTeam = group.team"
                  @view-details="detailTeam = group.team"
                />
              </div>
            </template>

            <!-- En Route -->
            <template v-if="fieldTeamGroupsEnroute.length > 0">
              <div class="flex flex-col items-center gap-1.5 shrink-0">
                <div class="caution-stripe w-full h-5 rounded flex items-center justify-center">
                  <span class="text-sm font-semibold text-on-surface-variant uppercase tracking-widest">En Route</span>
                </div>
              </div>
              <div class="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3">
                <FieldTeamCard
                  v-for="group in fieldTeamGroupsEnroute"
                  :key="group.team.team_id"
                  :incident-id="incidentId"
                  :op-id="opsStore.selectedOpId"
                  :team="group.team"
                  :leader="group.leader"
                  :members="group.members"
                  :can-update-status="canUpdateStatus"
                  :search-query="personnelSearch"
                  @debrief="debriefTeam = group.team"
                  @view-details="detailTeam = group.team"
                />
              </div>
            </template>

            <!-- In Assignment -->
            <template v-if="fieldTeamGroupsOnScene.length > 0">
              <div class="flex flex-col items-center gap-1.5 shrink-0">
                <div class="caution-stripe w-full h-5 rounded flex items-center justify-center">
                  <span class="text-sm font-semibold text-on-surface-variant uppercase tracking-widest">In Assignment</span>
                </div>
              </div>
              <div class="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3">
                <FieldTeamCard
                  v-for="group in fieldTeamGroupsOnScene"
                  :key="group.team.team_id"
                  :incident-id="incidentId"
                  :op-id="opsStore.selectedOpId"
                  :team="group.team"
                  :leader="group.leader"
                  :members="group.members"
                  :can-update-status="canUpdateStatus"
                  :search-query="personnelSearch"
                  @debrief="debriefTeam = group.team"
                  @view-details="detailTeam = group.team"
                />
              </div>
            </template>

            <!-- Returning to Base -->
            <template v-if="fieldTeamGroupsRTB.length > 0">
              <div class="flex flex-col items-center gap-1.5 shrink-0">
                <div class="caution-stripe w-full h-5 rounded flex items-center justify-center">
                  <span class="text-sm font-semibold text-on-surface-variant uppercase tracking-widest">Returning to Base</span>
                </div>
              </div>
              <div class="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3">
                <FieldTeamCard
                  v-for="group in fieldTeamGroupsRTB"
                  :key="group.team.team_id"
                  :incident-id="incidentId"
                  :op-id="opsStore.selectedOpId"
                  :team="group.team"
                  :leader="group.leader"
                  :members="group.members"
                  :can-update-status="canUpdateStatus"
                  :search-query="personnelSearch"
                  @debrief="debriefTeam = group.team"
                  @view-details="detailTeam = group.team"
                />
              </div>
            </template>

            <!-- Returned -->
            <template v-if="fieldTeamGroupsReturned.length > 0">
              <div class="flex flex-col items-center gap-1.5 shrink-0">
                <div class="caution-stripe w-full h-5 rounded flex items-center justify-center">
                  <span class="text-sm font-semibold text-on-surface-variant uppercase tracking-widest">Returned</span>
                </div>
              </div>
              <div class="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3">
                <FieldTeamCard
                  v-for="group in fieldTeamGroupsReturned"
                  :key="group.team.team_id"
                  :incident-id="incidentId"
                  :op-id="opsStore.selectedOpId"
                  :team="group.team"
                  :leader="group.leader"
                  :members="group.members"
                  :can-update-status="canUpdateStatus"
                  :search-query="personnelSearch"
                  @debrief="debriefTeam = group.team"
                  @view-details="detailTeam = group.team"
                />
              </div>
            </template>

            <p v-if="fieldTeamGroups.length === 0 && !canManageTeams" class="text-sm text-outline italic text-center py-4">
              No field teams yet. Create a team to start assigning personnel.
            </p>
          </div>
        </div>
    </template>
    <div
      v-else
      class="border border-border px-6 py-16 text-center"
    >
      <p class="font-mono text-xs text-outline uppercase tracking-widest">No operational period selected.</p>
    </div>
  </div>

  <CheckInModal v-model:visible="showCheckIn" :incident-id="incidentId" />
  <QRCodePanel v-model:visible="showQR" />
  <DebriefModal
    v-if="debriefTeam"
    :incident-id="incidentId"
    :op-id="opsStore.selectedOpId!"
    :team="debriefTeam"
    @close="debriefTeam = null"
  />

  <TeamDetailDialog
    v-model:visible="detailDialogVisible"
    :team="detailTeam"
    :incident-id="incidentId"
    :op-id="opsStore.selectedOpId ?? ''"
    @debrief="debriefTeam = detailTeam"
  />
</template>
